import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Calculator, Coins, TrendingUp, Clock, Award, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StakingCalculator = () => {
  const [amount, setAmount] = useState(10000);
  const [duration, setDuration] = useState(90);
  const [tier, setTier] = useState('silver');
  const [result, setResult] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const tiers = [
    { id: 'bronze', name: 'Bronze', minStake: 1000, apy: 8 },
    { id: 'silver', name: 'Silver', minStake: 10000, apy: 12 },
    { id: 'gold', name: 'Gold', minStake: 50000, apy: 18 },
    { id: 'platinum', name: 'Platinum', minStake: 100000, apy: 25 },
  ];

  useEffect(() => {
    axios.get(`${API}/token/info`).then(r => setTokenInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = tiers.reduce((acc, t) => amount >= t.minStake ? t : acc, tiers[0]);
    setTier(t.id);
  }, [amount]);

  const calculate = async () => {
    setCalculating(true);
    try {
      const r = await axios.post(`${API}/staking/calculate`, { amount, duration_days: duration, tier });
      setResult(r.data);
    } catch (e) { console.error(e); }
    setCalculating(false);
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="calculator-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-unbounded text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Calculator className="text-[#D4AF37]" /> Staking Calculator
          </h1>
          <p className="text-white/60">Calculate your potential earnings from staking AIH tokens.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="glass-panel p-6">
            <h2 className="font-unbounded text-xl font-bold text-white mb-6">Configure Stake</h2>
            
            <div className="mb-6">
              <Label className="text-white/80 mb-2 block">Amount (AIH)</Label>
              <Input 
                type="number" 
                value={amount} 
                onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value) || 0))} 
                className="input-cosmic text-2xl font-bold h-14" 
                data-testid="stake-amount-input" 
              />
              <div className="flex gap-2 mt-2">
                <Button variant="ghost" size="sm" className="text-xs text-white/60" onClick={() => setAmount(1000)}>1,000</Button>
                <Button variant="ghost" size="sm" className="text-xs text-white/60" onClick={() => setAmount(10000)}>10,000</Button>
                <Button variant="ghost" size="sm" className="text-xs text-white/60" onClick={() => setAmount(50000)}>50,000</Button>
                <Button variant="ghost" size="sm" className="text-xs text-white/60" onClick={() => setAmount(100000)}>100,000</Button>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between mb-3">
                <Label className="text-white/80">Duration</Label>
                <span className="text-[#D4AF37] font-semibold">{duration} days</span>
              </div>
              <Slider 
                value={[duration]} 
                onValueChange={(v) => setDuration(v[0])} 
                min={30} 
                max={365} 
                step={30} 
                data-testid="duration-slider" 
              />
              <div className="flex justify-between text-xs text-white/40 mt-1">
                <span>30 days</span>
                <span>365 days</span>
              </div>
            </div>

            <div className="mb-6">
              <Label className="text-white/80 mb-3 block">Tier</Label>
              <div className="grid grid-cols-2 gap-3">
                {tiers.map(t => (
                  <button 
                    key={t.id} 
                    onClick={() => setTier(t.id)} 
                    disabled={amount < t.minStake} 
                    className={`p-4 rounded-xl border transition-all ${
                      tier === t.id 
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                        : amount >= t.minStake 
                          ? 'border-white/10 hover:border-white/30' 
                          : 'border-white/5 opacity-50 cursor-not-allowed'
                    }`} 
                    data-testid={`tier-select-${t.id}`}
                  >
                    <span className={`tier-badge ${t.name.toLowerCase()} mb-2 inline-block`}>{t.name}</span>
                    <p className="text-xl font-bold text-white">{t.apy}% APY</p>
                    <p className="text-xs text-white/40">Min. {t.minStake.toLocaleString()}</p>
                  </button>
                ))}
              </div>
            </div>

            <Button 
              className="w-full btn-primary h-12" 
              onClick={calculate} 
              disabled={calculating || amount <= 0} 
              data-testid="calculate-btn"
            >
              {calculating ? 'Calculating...' : 'Calculate Earnings'}
            </Button>
          </div>

          <div className="glass-panel p-6" data-testid="results-panel">
            <h2 className="font-unbounded text-xl font-bold text-white mb-6">Results</h2>
            
            {result ? (
              <div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#0A1628]/80 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-white/60 mb-2">
                      <Coins size={16} />
                      <span className="text-sm">Principal</span>
                    </div>
                    <p className="font-unbounded text-xl font-bold text-white">{result.principal.toLocaleString()}</p>
                    <p className="text-xs text-white/40">AIH Tokens</p>
                  </div>

                  <div className="bg-[#0A1628]/80 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-white/60 mb-2">
                      <TrendingUp size={16} />
                      <span className="text-sm">APY Rate</span>
                    </div>
                    <p className="font-unbounded text-xl font-bold text-[#D4AF37]">{result.apy}%</p>
                    <p className="text-xs text-white/40">{result.tier} Tier</p>
                  </div>

                  <div className="bg-[#0A1628]/80 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-white/60 mb-2">
                      <Award size={16} />
                      <span className="text-sm">Earnings</span>
                    </div>
                    <p className="font-unbounded text-xl font-bold text-green-400">+{result.estimated_earnings.toLocaleString()}</p>
                    <p className="text-xs text-white/40">AIH Tokens</p>
                  </div>

                  <div className="bg-[#0A1628]/80 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-white/60 mb-2">
                      <Clock size={16} />
                      <span className="text-sm">Total After {result.duration_days}d</span>
                    </div>
                    <p className="font-unbounded text-xl font-bold text-white">{result.total_value.toLocaleString()}</p>
                    <p className="text-xs text-white/40">AIH Tokens</p>
                  </div>
                </div>

                {tokenInfo && (
                  <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white/60">Estimated USD Value</p>
                        <p className="font-unbounded text-2xl font-bold text-[#D4AF37]">
                          ${(result.total_value * tokenInfo.price).toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/40">At current price</p>
                        <p className="text-sm text-white/60">${tokenInfo.price} / AIH</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Calculator size={48} className="text-white/20 mb-4" />
                <p className="text-white/60">Configure your stake and click calculate to see projected earnings.</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 glass-panel p-6">
          <div className="flex items-start gap-3">
            <Info className="text-[#D4AF37] flex-shrink-0 mt-1" size={20} />
            <div>
              <h3 className="font-semibold text-white mb-2">How Staking Works</h3>
              <ul className="text-sm text-white/60 space-y-2">
                <li>• Stake your AIH tokens to earn passive rewards based on your tier level</li>
                <li>• Higher tiers offer better APY rates but require more tokens</li>
                <li>• Rewards are calculated daily and compound over time</li>
                <li>• All staking is backed by Bitcoin reserves for security</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StakingCalculator;
